import { TFunction } from 'i18next';
import { append } from 'ramda';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { filterUndefinedRows } from 'src/js/pages/statistic/statistic-mapper';
import { StatisticSectionViewModel } from 'src/js/pages/statistic/statistic-view-model';
import { formatUptime } from 'src/js/pages/streaming/stream/stream-helper';
import {
  formatLastOccuredTime,
  formatPreprocessorState,
  isNilOrEmpty,
  toTitleCase,
  toTitleCaseDefaultToString,
} from 'src/js/util/global-util';
import { deriveAudioLanguage } from '../stream/stream-statistics-content';

import { DecoderViewModel, decoderBufferingModeToString, translateDecoderState } from './decoder-model';
import { formatLast } from '../../dashboard/decoder-item-statistics';

const appendBytesToFirstItem = (value?: string | string[]): string | string[] | undefined => {
  if (!Array.isArray(value)) {
    return value;
  }

  if (value.length === 0) {
    return [undefined, undefined];
  }

  if (!value[0]) {
    return value;
  }

  return value.map((item, index) => (index === 0 ? `${item} bytes` : item));
};

export const mapDecoderToStatistics = (
  decoder: DecoderViewModel,
  fullPage: boolean,
  t: TFunction,
): StatisticSectionViewModel[] => {
  let sections: StatisticSectionViewModel[] = [
    {
      title: t('statistics.decoder.section.general'),
      rows: [
        {
          name: t('statistics.stream.rows.id'),
          value: decoder.id.toString(),
        },
        {
          name: t('general.status'),
          value: translateDecoderState(decoder.stats.state, decoder.stats.troubleCode, t),
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.uptime'),
          value: formatUptime(decoder.stats.uptime),
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.bufferingState'),
          value: toTitleCase(decoder.stats.bufferingState),
          shortlisted: true,
          when: !fullPage,
        },
        {
          name: t('statistics.decoder.rows.bufferingMode'),
          value: decoderBufferingModeToString(decoder.stats.bufferingMode, t),
          shortlisted: true,
          when: !fullPage,
        },
        {
          name: t('statistics.decoder.rows.videoLatency'),
          value: decoder.stats.videoLatency,
          shortlisted: true,
          when: !fullPage,
        },
        {
          name: t('statistics.decoder.rows.hdrTypeIn'),
          value: decoder.stats.hdrTypeIn,
          shortlisted: true,
          when: !fullPage && systemInfo.hasHDR,
        },
        {
          name: t('statistics.decoder.rows.hdrType'),
          value: decoder.stats.hdrType,
          shortlisted: true,
          when: !fullPage && systemInfo.hasHDR,
        },
        {
          name: t('statistics.decoder.rows.lastReset'),
          value: decoder.stats.lastReset,
          shortlisted: true,
        },
      ],
    },
    {
      title: t('statistics.decoder.section.video'),
      span: 3,
      rows: [
        {
          name: t('statistics.decoder.rows.videoAlgorithm'),
          value: toTitleCase(decoder.stats.videoAlgorithm),
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.videoChroma'),
          value: decoder.stats.videoChroma,
        },
        {
          name: t('statistics.decoder.rows.videoProfile'),
          value: decoder.stats.videoProfile,
        },
        {
          name: t('statistics.decoder.rows.videoLevel'),
          value: decoder.stats.videoLevel,
        },
        {
          name: t('statistics.decoder.rows.videoFraming'),
          value: decoder.stats.videoFraming,
        },
        {
          name: t('statistics.decoder.rows.videoSlicesPerFrame'),
          value: decoder.stats.videoSlicesPerFrame,
        },
        {
          name: t('statistics.decoder.rows.preprocessorState'),
          value: formatPreprocessorState(decoder.stats.preprocessorState),
        },
        {
          name: t('statistics.decoder.rows.unlicensedReason'),
          value: decoder.stats.unlicensedReason,
        },
        {
          name: t('statistics.decoder.rows.unsupportedReason'),
          value: decoder.stats.unsupportedReason,
        },
        {
          name: t('statistics.decoder.rows.vframerNumInputPackets'),
          value: decoder.stats.vframerNumInputPackets?.toString(),
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.videoDecoderState'),
          value: toTitleCaseDefaultToString(decoder.stats.videoDecoderState),
        },
        {
          name: t('statistics.decoder.rows.videoInputResolution'),
          value: decoder.stats.videoInputResolution,
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.videoDisplayFormat'),
          value: decoder.stats.videoDisplayFormat,
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.loadPercentage'),
          value: `${decoder.stats.loadPercentage}%`,
        },
        {
          name: t('statistics.decoder.rows.stillImage'),
          value: decoder.stats.stillImage,
        },
        {
          name: t('statistics.decoder.rows.corruptedFrames'),
          value: formatLast(decoder.stats.corruptedFrames),
          formatString: true,
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.corruptedFramesDropped'),
          value: formatLast(decoder.stats.corruptedFramesDropped),
          formatString: true,
        },
        {
          name: t('statistics.decoder.rows.displayedOutputFrames'),
          value: formatLast(decoder.stats.displayedOutputFrames),
          formatString: true,
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.skippedOutputFrames'),
          value: formatLast(decoder.stats.skippedOutputFrames),
          formatString: true,
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.replayedOutputFrames'),
          value: formatLast(decoder.stats.replayedOutputFrames),
          formatString: true,
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.videoOutputJitterMs'),
          value: decoder.stats.videoOutputJitterMs + ' ms',
          when: decoder.stats.videoOutputJitterMs !== undefined,
        },
        {
          name: t('statistics.decoder.rows.videoStcLeadTime'),
          value: [decoder.stats.videoStcLeadTime, decoder.stats.videoStcLeadTimeDetails],
          formatString: true,
        },
      ],
    },
    {
      title: t('statistics.decoder.section.buffering'),
      rows: [
        {
          name: t('statistics.decoder.rows.bufferingState'),
          value: toTitleCase(decoder.stats.bufferingState),
        },
        {
          name: t('statistics.decoder.rows.bufferingMode'),
          value: toTitleCase(decoder.stats.bufferingMode),
        },
        {
          name: t('statistics.decoder.rows.bufferingAdjustments'),
          value: formatLast(decoder.stats.bufferingAdjustments),
          formatString: true,
        },
        {
          name: t('statistics.decoder.rows.videoLatency'),
          value: decoder.stats.videoLatency,
        },
        {
          name: t('statistics.decoder.rows.stcToPcrLeadTime'),
          value: decoder.stats.stcToPcrLeadTime,
        },
      ],
    },
  ];

  if (decoder.stats.bufferingMode === 'MULTISYNC') {
    sections = append(
      {
        title: t('statistics.decoder.section.multisync'),
        rows: [
          {
            name: t('statistics.decoder.rows.multisyncStatus'),
            value: toTitleCase(decoder.stats.multisyncStatus),
          },
          {
            name: t('statistics.decoder.rows.multisyncDelayActual'),
            value: decoder.stats.multisyncDelayActual,
          },
          {
            name: t('statistics.decoder.rows.multisyncDelayRange'),
            value: decoder.stats.multisyncDelayRange,
          },
          {
            name: t('statistics.decoder.rows.multisyncDelaySet'),
            value: decoder.stats.multisyncDelaySet,
          },
          {
            name: t('statistics.decoder.rows.multisyncSystemTime'),
            value: decoder.stats.multisyncSystemTime,
          },
          {
            name: t('statistics.decoder.rows.multisyncTimecode'),
            value: decoder.stats.multisyncTimecode,
          },
          {
            name: t('statistics.decoder.rows.multisyncTimeDiff'),
            value: decoder.stats.multisyncTimeDiff,
          },
          {
            name: t('statistics.decoder.rows.multisyncTimecodePackets'),
            value: decoder.stats.multisyncTimecodePackets,
          },
          {
            name: t('statistics.decoder.rows.multisyncTransmissionTime'),
            value: decoder.stats.multisyncTransmissionTime,
          },
        ],
      },
      sections,
    );
  }

  sections = append(
    {
      title: t('statistics.decoder.section.clockRecovery'),
      rows: [
        {
          name: t('statistics.decoder.rows.clockTrackingMode'),
          value: toTitleCase(decoder.stats.clockTrackingMode),
          when: decoder.stats.clockTrackingMode === 'ENABLE',
        },
        {
          name: t('statistics.decoder.rows.clockStatus'),
          value: toTitleCase(decoder.stats.clockStatus),
          when: decoder.stats.clockTrackingMode === 'ENABLE',
        },
        {
          name: t('statistics.decoder.rows.clockReSyncCount'),
          value: decoder.stats.clockReSyncCount?.toString(),
          when: decoder.stats.clockTrackingMode === 'ENABLE',
        },
        {
          name: t('statistics.decoder.rows.clockCurrentStc'),
          value: decoder.stats.clockCurrentStc,
          when: decoder.stats.clockTrackingMode === 'ENABLE',
        },
        {
          name: t('statistics.decoder.rows.clockStcAvg'),
          value: decoder.stats.clockStcAvg,
          when: decoder.stats.clockTrackingMode === 'ENABLE',
        },
      ],
    },
    sections,
  );

  if (decoder.stats.klv) {
    sections = append(
      {
        title:
          t('statistics.decoder.section.klv') +
          (decoder.stats.klvUnlicensed ? ' (' + t('general.unlicensed') + ')' : ''),
        rows: [
          {
            name: t('statistics.decoder.rows.receivedPackets'),
            value: formatLast(decoder.stats.klvReceivedPackets),
            formatString: true,
          },
          {
            name: t('statistics.decoder.rows.outputPackets'),
            value: formatLast(decoder.stats.klvOutputPackets),
            formatString: true,
          },
        ],
      },
      sections,
    );
  }

  if (decoder.stats.closedCaption) {
    sections = append(
      {
        title: t('statistics.decoder.section.cc'),
        rows: [
          {
            name: t('statistics.decoder.rows.receivedPackets'),
            value: formatLast(decoder.stats.ccReceivedPackets),
            formatString: true,
          },
          {
            name: t('statistics.decoder.rows.outputPackets'),
            value: formatLast(decoder.stats.ccOutputPackets),
            formatString: true,
          },
          {
            name: t('statistics.decoder.rows.payloadBytes'),
            value: decoder.stats.ccPayloadBytes,
          },
          {
            name: t('statistics.decoder.rows.maxPayloadSize'),
            value: appendBytesToFirstItem(formatLast(decoder.stats.ccMaxPayloadSize)),
            formatString: true,
          },
          {
            name: t('statistics.decoder.rows.latency'),
            value: decoder.stats.ccLatency,
            shortlisted: false,
          },
        ],
      },
      sections,
    );
  }

  if (decoder.stats.timeCode) {
    sections = append(
      {
        title: t('statistics.decoder.section.tc'),
        rows: [
          {
            name: t('statistics.decoder.rows.receivedPackets'),
            value: formatLast(decoder.stats.tcReceivedPackets),
            formatString: true,
          },
          {
            name: t('statistics.decoder.rows.outputPackets'),
            value: formatLast(decoder.stats.tcOutputPackets),
            formatString: true,
          },
          {
            name: t('statistics.decoder.rows.tcTimecodeValue'),
            value: decoder.stats.tcTimecodeValue,
          },
        ],
      },
      sections,
    );
  }

  if (decoder.stats.afd) {
    sections = append(
      {
        title: t('statistics.decoder.section.afd'),
        rows: [
          {
            name: t('statistics.decoder.rows.receivedPackets'),
            value: formatLast(decoder.stats.afdReceivedPackets),
            formatString: true,
          },
          {
            name: t('statistics.decoder.rows.outputPackets'),
            value: formatLast(decoder.stats.afdOutputPackets),
            formatString: true,
          },
        ],
      },
      sections,
    );
  }

  if (decoder.stats.scte) {
    sections = append(
      {
        title: t('statistics.decoder.section.scte'),
        rows: [
          {
            name: t('statistics.decoder.rows.scteState'),
            value: decoder.stats.scteState,
            shortlisted: true,
          },
          {
            name: t('statistics.decoder.rows.scteReceivedPackets'),
            value: formatLast(decoder.stats.scteReceivedPackets),
            formatString: true,
            shortlisted: true,
          },
          {
            name: t('statistics.decoder.rows.scteOutputPackets'),
            value: formatLast(decoder.stats.scteOutputPackets),
            formatString: true,
            shortlisted: true,
          },
          {
            name: t('statistics.decoder.rows.sctePayloadBytes'),
            value: decoder.stats.sctePayloadBytes,
            shortlisted: true,
          },
          {
            name: t('statistics.decoder.rows.scteMaxPayloadSize'),
            value: appendBytesToFirstItem(formatLast(decoder.stats.scteMaxPayloadSize)),
            formatString: true,
            shortlisted: false,
          },
          {
            name: t('statistics.decoder.rows.scteFreedPackets'),
            value: decoder.stats.scteFreedPackets,
            shortlisted: false,
          },
          {
            name: t('statistics.decoder.rows.scteLatency'),
            value: decoder.stats.scteLatency,
            shortlisted: true,
          },
        ],
      },
      sections,
    );
  }

  if (systemInfo.hasHDR) {
    sections = append(
      {
        title: t('statistics.decoder.section.hdr'),
        rows: [
          {
            name: t('statistics.decoder.rows.hdrTypeIn'),
            value: decoder.stats.hdrTypeIn,
          },
          {
            name: t('statistics.decoder.rows.hdrType'),
            value: decoder.stats.hdrType,
          },
          {
            name: t('statistics.decoder.rows.hdrColourPrimaries'),
            value: decoder.stats.hdrColourPrimaries,
          },
          {
            name: t('statistics.decoder.rows.hdrTransferCharacteristics'),
            value: decoder.stats.hdrTransferCharacteristics,
          },
          {
            name: t('statistics.decoder.rows.hdrMatrixCoefficients'),
            value: decoder.stats.hdrMatrixCoefficients,
          },
        ],
      },
      sections,
    );
  }

  sections = append(
    {
      title: t('statistics.decoder.section.audio'),
      sectionBreak: true,
      sectionBreakTitle: t('statistics.decoder.audioTitle'),
      rows: [
        {
          name: t('statistics.decoder.rows.audioState'),
          value: toTitleCaseDefaultToString(decoder.stats.audioState),
        },
        {
          name: t('statistics.decoder.rows.audioPairsAmount'),
          value: decoder.stats.audioPairsAmount?.toString(),
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.downmixSurround'),
          value: decoder.stats.downmixSurround ? t('general.yes') : t('general.no'),
          when: decoder.stats.downmixSurround !== undefined,
        },
        {
          name: t('statistics.decoder.rows.audioDecodedFrames'),
          value: formatLast(decoder.stats.audioDecodedFrames),
          formatString: true,
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.audioPlayedFrames'),
          value: formatLast(decoder.stats.audioPlayedFrames),
          formatString: true,
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.audioSkippedFrames'),
          value: formatLast(decoder.stats.audioSkippedFrames),
          formatString: true,
          shortlisted: true,
        },
        {
          name: t('statistics.decoder.rows.audioMode'),
          value: decoder.stats.audioPairs?.[0].mode,
          shortlisted: true,
          when: !fullPage && decoder.stats.audioPairsAmount > 0 && !isNilOrEmpty(decoder.stats.audioPairs),
        },
        {
          name: t('statistics.decoder.rows.audioCompression'),
          value: decoder.stats.audioPairs?.[0].compression,
          shortlisted: true,
          when: !fullPage && decoder.stats.audioPairsAmount > 0 && !isNilOrEmpty(decoder.stats.audioPairs),
        },
        {
          name: t('statistics.decoder.rows.audioBitrate'),
          value: decoder.stats.audioPairs?.[0].bitrate != '0' ? decoder.stats.audioPairs?.[0].bitrate : '0 kbps',
          shortlisted: true,
          when: !fullPage && decoder.stats.audioPairsAmount > 0 && !isNilOrEmpty(decoder.stats.audioPairs),
        },
      ],
    },
    sections,
  );

  if (decoder.stats.audioPairsAmount > 0 && !isNilOrEmpty(decoder.stats.audioPairs)) {
    decoder.stats.audioPairs.map((pair, index) => {
      pair.index = index; // for the label " on SDI Pair X"
    });
    // Group audio pairs by nDecoderIndex

    type AudioPair = (typeof decoder.stats.audioPairs)[0];
    const audioPairsByDecoderIndex = new Map<number, AudioPair[]>();

    for (const audioPair of decoder.stats.audioPairs) {
      const decoderIndex = audioPair.nDecoderIndex;
      if (!audioPairsByDecoderIndex.has(decoderIndex)) {
        audioPairsByDecoderIndex.set(decoderIndex, []);
      }
      audioPairsByDecoderIndex.get(decoderIndex).push(audioPair);
    }

    // Create a panel for each decoder index group
    let decoderIndexCounter = 0;
    for (const [, audioPairs] of Array.from(audioPairsByDecoderIndex.entries())) {
      decoderIndexCounter++;
      const firstAudioPair = audioPairs[0]; // Use first pair for common stats
      let allOutputLayouts = firstAudioPair.outputLayoutDetailed;
      if (
        audioPairs.length >= 1 &&
        audioPairs[0].streamOutputLayoutType != null &&
        audioPairs[0].streamOutputLayoutType != ''
      ) {
        allOutputLayouts = audioPairs[0].streamOutputLayoutType;
      } else if (audioPairs.length >= 2 && audioPairs[0].outputLayoutDetailed !== audioPairs[1].outputLayoutDetailed) {
        allOutputLayouts = audioPairs
          .map((pair) => pair.outputLayoutDetailed) // R4KD-2831
          .reduce((acc, layout) => acc + ' ' + layout)
          .trim();
      }

      sections = append(
        {
          title: t('statistics.decoder.section.audioPair', { id: decoderIndexCounter }),
          span: 2,
          rows: [
            {
              name: t('statistics.decoder.rows.audioCompression'),
              value: firstAudioPair.compression,
            },
            {
              name: t('statistics.decoder.rows.audioBitrate'),
              value: firstAudioPair.bitrate != '0' ? firstAudioPair.bitrate : '0 kbps',
            },
            {
              name: t('statistics.decoder.rows.language'),
              value: deriveAudioLanguage(firstAudioPair.language) ?? t('general.none'),
            },
            /* "Surround Sound Pair" not needed anymore, since we group all in one section, R4KD-2157
            {
              name: t('statistics.decoder.rows.surroundPair'),
              value: firstAudioPair.surroundPair,
              when: decoder.stats.downmixSurround === false,
            },*/
            {
              name: t('statistics.decoder.rows.audioInputLayout'),
              value: [firstAudioPair.inputLayout, firstAudioPair.inputLayoutDetailed],
              formatString: true,
            },
            {
              name: t('statistics.decoder.rows.audioOutputLayout'),
              value: [firstAudioPair.outputLayout, allOutputLayouts],
              formatString: true,
            },
            {
              name: t('statistics.decoder.rows.sampleRateIn'),
              value: firstAudioPair.sampleRateIn != 0 ? firstAudioPair.sampleRateIn?.toString() + ' kHz' : 'N/A',
            },
            {
              name: t('statistics.decoder.rows.sampleRateOut'),
              value: firstAudioPair.sampleRateOut != 0 ? firstAudioPair.sampleRateOut?.toString() + ' kHz' : 'N/A',
            },
            // Display dB levels for each audio pair individually since they are unique per pair
            ...audioPairs.flatMap((audioPair, pairIndex) => [
              {
                name: t('statistics.decoder.rows.outputPair', { index: pairIndex + 1 }),
                value: [
                  `${audioPair.outputLayoutDetailed}`,
                  ` ${
                    systemInfoCapabilities.is2110() ? t('statistics.decoder.onSdi_2110') : t('statistics.decoder.onSdi')
                  } ${audioPair.index + 1}`,
                ],
                formatString: true,
              },
              {
                name: `\u00A0\u00A0\u00A0\u00A0${t('statistics.decoder.rows.dbLevelLeft')}`,
                value: [
                  `${audioPair.dbLeft}dB`,
                  `max=${audioPair.dbLeftMax}dB, ${formatLastOccuredTime(audioPair.dbLeftMaxLastTimeSec, t)}`,
                ],
                when: audioPair.dbLeft !== undefined,
                formatString: true,
              },
              {
                name: `\u00A0\u00A0\u00A0\u00A0${t('statistics.decoder.rows.dbLevelRight')}`,
                value: [
                  `${audioPair.dbRight}dB`,
                  `max=${audioPair.dbRightMax}dB, ${formatLastOccuredTime(audioPair.dbRightMaxLastTimeSec, t)}`,
                ],
                when: audioPair.dbRight !== undefined,
                formatString: true,
              },
            ]),
            {
              name: t('statistics.decoder.rows.avSyncMs'),
              value: firstAudioPair.avSyncMs != 0 ? firstAudioPair.avSyncMs?.toString() + ' ms' : '0 ms',
            },
            {
              name: t('statistics.decoder.rows.audioStcLeadTime'),
              value: [firstAudioPair.stcLeadTime, firstAudioPair.stcLeadTimeDetails],
              formatString: true,
            },
            {
              name: t('statistics.decoder.rows.discontinuities'),
              value: firstAudioPair.discontinuities?.toString(),
            },
            {
              name: t('statistics.decoder.rows.decodeErrors'),
              value: formatLast(firstAudioPair.decodeErrors?.toString()),
              formatString: true,
            },
            {
              name: t('statistics.decoder.rows.outputErrors'),
              value: formatLast(firstAudioPair.outputErrors?.toString()),
              formatString: true,
            },
          ],
        },
        sections,
      );
    }
  }

  // remove undefined rows
  return filterUndefinedRows(sections);
};
